<template>
  <div>
    <div class="blog-listing">
      <header class="blog-listing__header">
        <h1>{{ heading }}</h1>
        <p class="blog-listing__standfirst">{{ standfirst }}</p>
        <!-- Only from page 2 on: page 1's heading should not be qualified. -->
        <p v-if="currentPage > 1" class="blog-listing__page-position">
          Page {{ currentPage }} of {{ totalPages }}
        </p>
      </header>

      <TagNav :tags="hubTags" :active-slug="activeTag ? activeTag.slug : ''" />

      <ul v-if="posts.length" class="blog-listing__grid">
        <li v-for="(post, index) in posts" :key="post.slug">
          <nuxt-link :to="`/blog/${post.slug}`" class="blog-listing__link">
            <!-- First row is above the fold on every breakpoint. -->
            <ListItem :post="post" :eager="index < 3" />
          </nuxt-link>
        </li>
      </ul>

      <p v-else class="blog-listing__empty">
        No articles to show right now. Please check back soon.
      </p>

      <Pagination
        :current-page="currentPage"
        :total-pages="totalPages"
        :tag-slug="activeTag ? activeTag.slug : ''"
      />
    </div>

    <PromotionBanner />
  </div>
</template>

<script>
import ListItem from '@/components/blog/list-item'
import Pagination from '@/components/blog/Pagination'
import TagNav from '@/components/blog/TagNav'
import PromotionBanner from '@/components/PromotionBanner'

export default {
  name: 'BlogListing',
  components: { ListItem, Pagination, TagNav, PromotionBanner },
  props: {
    posts: { type: Array, default: () => [] },
    currentPage: { type: Number, default: 1 },
    totalPages: { type: Number, default: 1 },
    hubTags: { type: Array, default: () => [] },
    // Set on a tag hub; null on the main listing.
    activeTag: { type: Object, default: null },
  },
  computed: {
    heading() {
      return this.activeTag ? this.activeTag.name : 'Welcome to PS.'
    },
    standfirst() {
      if (!this.activeTag) {
        return 'Articles, news and general ramblings from the Pedalsure team.'
      }

      const count = this.activeTag.count
      return `${count} ${count === 1 ? 'article' : 'articles'} on ${
        this.activeTag.name
      } from the Pedalsure team.`
    },
  },
}
</script>

<style lang="scss" scoped>
.blog-listing {
  // Was 750px, which forced a two-up grid of very narrow cards. The wider
  // measure is what allows three columns on desktop.
  max-width: 1120px;
  margin: 0 auto;
  padding: 40px 20px 8px;

  @include tablet {
    padding: 56px 24px 8px;
  }
}

.blog-listing__header {
  margin-bottom: 32px;
  text-align: center;

  h1 {
    margin: 0;
    font-size: var(--ps-text-h2);
    line-height: 1.1;
    font-weight: 700;
    color: var(--ps-indigo);

    @include tablet {
      font-size: var(--ps-text-h1);
    }
  }
}

.blog-listing__standfirst {
  margin: 12px auto 0;
  max-width: 52ch;
  font-size: var(--ps-text-body);
  line-height: 1.5;
  color: var(--ps-body);
}

.blog-listing__page-position {
  margin: 12px 0 0;
  font-size: var(--ps-text-label);
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-weight: 600;
  color: var(--ps-muted);
}

.blog-listing__grid {
  display: grid;
  // Single column on phones, widening to two then three. Grid rather than
  // Bulma columns so the cards stretch to equal height per row.
  grid-template-columns: 1fr;
  gap: 24px;
  margin: 0;
  padding: 0;
  list-style: none;

  @include tablet {
    grid-template-columns: repeat(2, 1fr);
  }

  @include desktop {
    grid-template-columns: repeat(3, 1fr);
  }

  li {
    display: flex;
  }
}

.blog-listing__link {
  display: flex;
  width: 100%;
  text-decoration: none;
  border-radius: var(--ps-radius-lg);

  &:focus-visible {
    outline: none;
  }
}

.blog-listing__empty {
  text-align: center;
  font-size: var(--ps-text-body);
  color: var(--ps-muted);
  padding: 48px 0;
}
</style>
